import React, { useEffect, useState } from 'react';
import { AlertTriangle, Plus, X, Save, Trash2 } from 'lucide-react';
import PrintButton from '../components/PrintButton';
import PatientDropdown from '../components/PatientDropdown';

function Allergies({ user }) {
  const [patients, setPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [allergies, setAllergies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    allergen: '',
    allergyType: '',
    substance: '',
    reaction: '',
    reactionType: '',
    severity: '',
    criticalityLevel: '',
    verificationStatus: '',
    source: '',
    onsetDate: '',
    manifestation: [],
    notes: '',
    snomedCode: '',
    snomedReactionCode: '',
    rxNormCode: '',
    icd10Code: '',
    clinicalStatus: '',
  });

  useEffect(() => {
    async function load() {
      try {
        const data = await window.zignagenixAPI.getPatients();
        setPatients(Array.isArray(data) ? data : []);
        if (Array.isArray(data) && data.length > 0) {
          setSelectedPatientId(data[0].id);
          const aData = await window.zignagenixAPI.getAllergies(data[0].id);
          setAllergies(Array.isArray(aData) ? aData : []);
        }
      } catch (err) {
        console.error('Failed to load:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handlePatientChange(patientId) {
    setSelectedPatientId(patientId);
    try {
      const aData = await window.zignagenixAPI.getAllergies(patientId);
      setAllergies(Array.isArray(aData) ? aData : []);
    } catch (err) {
      console.error('Failed to load allergies:', err);
    }
  }

  const resetForm = () => {
    setForm({
      allergen: '',
      allergyType: '',
      substance: '',
      reaction: '',
      reactionType: '',
      severity: '',
      criticalityLevel: '',
      verificationStatus: '',
      source: '',
      onsetDate: '',
      manifestation: [],
      notes: '',
      snomedCode: '',
      snomedReactionCode: '',
      rxNormCode: '',
      icd10Code: '',
      clinicalStatus: '',
    });
    setError(null);
  };

  const handleSubmit = async () => {
    if (!form.allergen.trim()) {
      setError('Allergen is required');
      return;
    }
    try {
      await window.zignagenixAPI.createAllergy({
        patientId: selectedPatientId,
        ...form,
      });
      const aData = await window.zignagenixAPI.getAllergies(selectedPatientId);
      setAllergies(Array.isArray(aData) ? aData : []);
      setShowForm(false);
      resetForm();
    } catch (err) {
      setError('Failed to save allergy');
      console.error(err);
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Are you sure you want to remove this allergy record?')) return;
    try {
      await window.zignagenixAPI.deactivateAllergy(id);
      const aData = await window.zignagenixAPI.getAllergies(selectedPatientId);
      setAllergies(Array.isArray(aData) ? aData : []);
    } catch (err) {
      console.error('Failed to deactivate allergy:', err);
    }
  };

  const hasCreatePermission = user?.permissions?.includes('allergies:create');
  const hasDeletePermission = user?.permissions?.includes('allergies:delete');

  const manifestationOptions = [
    'Urticaria', 'Bronchospasm', 'Angioedema', 'Anaphylaxis',
    'Rash', 'Nausea', 'Vomiting', 'Diarrhea', 'Pruritus',
    'Swelling', 'Shortness of Breath', 'Wheezing', 'Other'
  ];

  const toggleManifestation = (manifestationValue) => {
    setForm(prev => ({
      ...prev,
      manifestation: prev.manifestation.includes(manifestationValue)
        ? prev.manifestation.filter(m => m !== manifestationValue)
        : [...prev.manifestation, manifestationValue]
    }));
  };

  const getAllergyTypeBadgeColor = (allergyType) => {
    const colors = {
      'Drug': '#2E8B98',
      'Food': '#62BB46',
      'Environmental': '#6C757D',
      'Latex': '#F26522',
      'Biologic': '#2E8B98',
      'Other': '#6C757D'
    };
    return colors[allergyType] || '#6C757D';
  };

  if (loading) return <div className="page-loading">Loading allergies...</div>;

  return (
    <div className="page">
      <div className="page-header-row">
        <h1 className="page-title">Allergies</h1>
        <PrintButton pageTitle="Allergies" />
      </div>

      <div className="filter-bar">
        <label className="filter-label">Patient:</label>
        <PatientDropdown
          patients={patients}
          value={String(selectedPatientId)}
          onChange={val => handlePatientChange(Number(val))}
          placeholder="Select a patient"
          idPrefix="allergies"
        />

        {hasCreatePermission && selectedPatientId && (
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            <Plus size={18} /> Add Allergy
          </button>
        )}
      </div>

      {showForm && (
        <div className="form-section">
          <h2 className="form-section-title">Add Allergy</h2>

          {error && (
            <div className="alert alert--error">{error}</div>
          )}

          {/* Row 1: Allergy Type, Allergen, Substance/Drug Class */}
          {/* FHIR R4 AllergyIntolerance Category Mapping: Drug→medication, Food→food, Environmental→environment, Latex→environment, Biologic→biologic, Other→other */}
          <div className="patient-form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="form-group">
              <label className="form-label">Allergy Type *</label>
              <select className="form-input" value={form.allergyType}
                onChange={(e) => setForm(prev => ({ ...prev, allergyType: e.target.value }))}>
                <option value="">Select type...</option>
                <option value="Drug">Drug</option>
                <option value="Food">Food</option>
                <option value="Environmental">Environmental</option>
                <option value="Latex">Latex</option>
                <option value="Biologic">Biologic</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Allergen *</label>
              <input type="text" className="form-input" value={form.allergen}
                onChange={(e) => setForm(prev => ({ ...prev, allergen: e.target.value }))}
                placeholder="e.g., Penicillin, Peanuts, Latex" />
            </div>
            <div className="form-group">
              <label className="form-label">Substance/Drug Class</label>
              <input type="text" className="form-input" value={form.substance}
                onChange={(e) => setForm(prev => ({ ...prev, substance: e.target.value }))}
                placeholder="e.g., Penicillins, Sulfonamides" />
            </div>
          </div>

          {/* Row 2: Reaction Type, Severity, Criticality Level */}
          <div className="patient-form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="form-group">
              <label className="form-label">Reaction Type</label>
              <select className="form-input" value={form.reactionType}
                onChange={(e) => setForm(prev => ({ ...prev, reactionType: e.target.value }))}>
                <option value="">Select reaction type...</option>
                <option value="Allergy">Allergy</option>
                <option value="Adverse Reaction">Adverse Reaction</option>
                <option value="Intolerance">Intolerance</option>
                <option value="Side Effect">Side Effect</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Severity</label>
              <select className="form-input" value={form.severity}
                onChange={(e) => setForm(prev => ({ ...prev, severity: e.target.value }))}>
                <option value="">Select severity...</option>
                <option value="Mild">Mild</option>
                <option value="Moderate">Moderate</option>
                <option value="Severe">Severe</option>
                <option value="Life Threatening">Life Threatening</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Criticality Level</label>
              <select className="form-input" value={form.criticalityLevel}
                onChange={(e) => setForm(prev => ({ ...prev, criticalityLevel: e.target.value }))}>
                <option value="">Select criticality...</option>
                <option value="Low">Low</option>
                <option value="High">High</option>
                <option value="Unable to Assess">Unable to Assess</option>
              </select>
            </div>
          </div>

          {/* Row 3: Verification Status, Source of Info, Onset Date */}
          <div className="patient-form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="form-group">
              <label className="form-label">Verification Status</label>
              <select className="form-input" value={form.verificationStatus}
                onChange={(e) => setForm(prev => ({ ...prev, verificationStatus: e.target.value }))}>
                <option value="">Select status...</option>
                <option value="Unverified">Unverified</option>
                <option value="Verified">Verified</option>
                <option value="Entered in Error">Entered in Error</option>
                <option value="Refuted">Refuted</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Source of Information</label>
              <select className="form-input" value={form.source}
                onChange={(e) => setForm(prev => ({ ...prev, source: e.target.value }))}>
                <option value="">Select source...</option>
                <option value="Patient Reported">Patient Reported</option>
                <option value="Family Reported">Family Reported</option>
                <option value="Provider Observed">Provider Observed</option>
                <option value="Medical Record">Medical Record</option>
                <option value="Pharmacy">Pharmacy</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Onset Date</label>
              <input type="date" className="form-input" value={form.onsetDate}
                onChange={(e) => setForm(prev => ({ ...prev, onsetDate: e.target.value }))} />
            </div>
          </div>

          {/* Coding Standards Section */}
          <h3 style={{ marginTop: '24px', marginBottom: '16px', fontSize: '14px', fontWeight: '600', color: '#333', borderBottom: '2px solid #2E8B98', paddingBottom: '8px' }}>Coding Standards</h3>

          {/* Row 4: SNOMED CT Code, SNOMED Reaction Code */}
          <div className="patient-form-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
            <div className="form-group">
              <label className="form-label">SNOMED CT Code</label>
              <input type="text" className="form-input" value={form.snomedCode}
                onChange={(e) => setForm(prev => ({ ...prev, snomedCode: e.target.value }))}
                placeholder="e.g., 91936005 (Penicillin allergy)" />
            </div>
            <div className="form-group">
              <label className="form-label">Reaction SNOMED Code</label>
              <input type="text" className="form-input" value={form.snomedReactionCode}
                onChange={(e) => setForm(prev => ({ ...prev, snomedReactionCode: e.target.value }))}
                placeholder="e.g., 39579001 (Anaphylaxis)" />
            </div>
          </div>

          {/* Row 5: RxNorm Code (conditional), ICD-10-CM Code */}
          <div className="patient-form-grid" style={{ gridTemplateColumns: form.allergyType === 'Drug' ? 'repeat(2, 1fr)' : '1fr' }}>
            {form.allergyType === 'Drug' && (
              <div className="form-group">
                <label className="form-label">RxNorm Code</label>
                <input type="text" className="form-input" value={form.rxNormCode}
                  onChange={(e) => setForm(prev => ({ ...prev, rxNormCode: e.target.value }))}
                  placeholder="e.g., 7980 (Penicillin V)" />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">ICD-10-CM Code</label>
              <input type="text" className="form-input" value={form.icd10Code}
                onChange={(e) => setForm(prev => ({ ...prev, icd10Code: e.target.value }))}
                placeholder="e.g., Z88.0 (Penicillin allergy status)" />
            </div>
          </div>

          {/* Row 6: Clinical Status */}
          <div className="patient-form-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div className="form-group">
              <label className="form-label">Clinical Status</label>
              <select className="form-input" value={form.clinicalStatus}
                onChange={(e) => setForm(prev => ({ ...prev, clinicalStatus: e.target.value }))}>
                <option value="">Select status...</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Resolved">Resolved</option>
              </select>
            </div>
          </div>

          {/* Full-width: Clinical Manifestation */}
          <div className="form-group">
            <label className="form-label">Clinical Manifestation</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
              {manifestationOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => toggleManifestation(option)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '16px',
                    border: form.manifestation.includes(option) ? '2px solid #2E8B98' : '2px solid #ddd',
                    backgroundColor: form.manifestation.includes(option) ? '#E8F4F8' : '#F8F9FA',
                    color: form.manifestation.includes(option) ? '#2E8B98' : '#666',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: form.manifestation.includes(option) ? '600' : '400',
                    transition: 'all 0.2s'
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          {/* Full-width: Additional Notes */}
          <div className="form-group">
            <label className="form-label">Additional Notes</label>
            <textarea className="form-input" value={form.notes}
              onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Enter additional clinical notes or observations..."
              rows="4"
              style={{ fontFamily: 'inherit', resize: 'vertical' }} />
          </div>

          <div className="form-actions">
            <button className="btn-primary" onClick={handleSubmit}>
              <Save size={18} /> Save Allergy
            </button>
            <button className="btn-ghost" onClick={() => { setShowForm(false); resetForm(); }}>
              <X size={18} /> Cancel
            </button>
          </div>
        </div>
      )}

      <div className="allergy-cards">
        {allergies.map((a) => (
          <div key={a.id} className={`allergy-card allergy-card--${a.severity?.toLowerCase() || 'mild'}`}>
            <div className="allergy-card-header">
              <AlertTriangle size={18} />
              <span className="allergy-card-name">{a.allergen}</span>
              {a.allergyType && (
                <span
                  style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    backgroundColor: getAllergyTypeBadgeColor(a.allergyType),
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: '600',
                    marginLeft: '8px'
                  }}
                >
                  {a.allergyType}
                </span>
              )}
              {a.verificationStatus && (
                <span
                  style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    backgroundColor: a.verificationStatus === 'Verified' ? '#62BB46' : a.verificationStatus === 'Refuted' ? '#DC3545' : '#F26522',
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: '600',
                    marginLeft: '4px'
                  }}
                >
                  {a.verificationStatus}
                </span>
              )}
              {a.severity && <span className={`severity-badge severity-badge--${a.severity.toLowerCase()}`}>{a.severity}</span>}
              {a.clinicalStatus && (
                <span
                  style={{
                    display: 'inline-block',
                    padding: '3px 8px',
                    borderRadius: '10px',
                    backgroundColor: a.clinicalStatus === 'Active' ? '#2E8B98' : a.clinicalStatus === 'Inactive' ? '#6C757D' : '#62BB46',
                    color: '#fff',
                    fontSize: '11px',
                    fontWeight: '600',
                    marginLeft: '4px'
                  }}
                >
                  {a.clinicalStatus}
                </span>
              )}
              {hasDeletePermission && (
                <button
                  className="btn-icon-danger"
                  onClick={() => handleDeactivate(a.id)}
                  title="Remove allergy"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '8px', fontSize: '14px' }}>
              {a.substance && <div><strong>Drug Class:</strong> {a.substance}</div>}
              {a.reactionType && <div><strong>Reaction Type:</strong> {a.reactionType}</div>}
              {a.criticalityLevel && (
                <div>
                  <strong>Criticality:</strong>
                  <span
                    style={{
                      marginLeft: '4px',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      backgroundColor: a.criticalityLevel === 'High' ? '#FFEBEE' : '#E8F5E9',
                      color: a.criticalityLevel === 'High' ? '#C62828' : '#2E7D32',
                      fontSize: '12px'
                    }}
                  >
                    {a.criticalityLevel}
                  </span>
                </div>
              )}
            </div>
            {a.reaction && <div className="allergy-card-reaction" style={{ marginTop: '8px' }}>Reaction: {a.reaction}</div>}
            {(a.snomedCode || a.snomedReactionCode || a.rxNormCode || a.icd10Code) && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {a.snomedCode && (
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: '#E8F4F8',
                        color: '#2E8B98',
                        fontSize: '10px',
                        fontWeight: '600',
                        border: 'none',
                        boxShadow: '3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85)',
                        whiteSpace: 'nowrap'
                      }}
                      title={`SNOMED CT: ${a.snomedCode}`}
                    >
                      SNOMED: {a.snomedCode}
                    </span>
                  )}
                  {a.snomedReactionCode && (
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: '#F0E8FF',
                        color: '#6C40C7',
                        fontSize: '10px',
                        fontWeight: '600',
                        border: 'none',
                        boxShadow: '3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85)',
                        whiteSpace: 'nowrap'
                      }}
                      title={`SNOMED Reaction: ${a.snomedReactionCode}`}
                    >
                      Reaction: {a.snomedReactionCode}
                    </span>
                  )}
                  {a.rxNormCode && (
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: '#E8F8F0',
                        color: '#00755A',
                        fontSize: '10px',
                        fontWeight: '600',
                        border: 'none',
                        boxShadow: '3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85)',
                        whiteSpace: 'nowrap'
                      }}
                      title={`RxNorm: ${a.rxNormCode}`}
                    >
                      RxNorm: {a.rxNormCode}
                    </span>
                  )}
                  {a.icd10Code && (
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: '#FFF4E8',
                        color: '#C85A17',
                        fontSize: '10px',
                        fontWeight: '600',
                        border: 'none',
                        boxShadow: '3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85)',
                        whiteSpace: 'nowrap'
                      }}
                      title={`ICD-10-CM: ${a.icd10Code}`}
                    >
                      ICD-10: {a.icd10Code}
                    </span>
                  )}
                </div>
              </div>
            )}
            {a.manifestation && a.manifestation.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <strong style={{ fontSize: '13px' }}>Manifestation:</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                  {Array.isArray(a.manifestation) ? a.manifestation.map((m, idx) => (
                    <span
                      key={idx}
                      style={{
                        display: 'inline-block',
                        padding: '3px 8px',
                        borderRadius: '12px',
                        backgroundColor: '#F0F0F0',
                        color: '#333',
                        fontSize: '12px',
                        border: 'none',
                        boxShadow: '3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85)'
                      }}
                    >
                      {m}
                    </span>
                  )) : (
                    <span style={{
                      display: 'inline-block',
                      padding: '3px 8px',
                      borderRadius: '12px',
                      backgroundColor: '#F0F0F0',
                      color: '#333',
                      fontSize: '12px',
                      border: 'none',
                      boxShadow: '3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85)'
                    }}>
                      {a.manifestation}
                    </span>
                  )}
                </div>
              </div>
            )}
            {a.source && <div style={{ marginTop: '8px', fontSize: '13px' }}><strong>Source:</strong> {a.source}</div>}
            {a.onset_date && <div className="allergy-card-onset" style={{ marginTop: '8px' }}>Onset: {a.onset_date}</div>}
            {a.notes && <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#FFFAF0', borderRadius: '4px', fontSize: '13px', borderLeft: '3px solid #F26522' }}><strong>Notes:</strong> {a.notes}</div>}
          </div>
        ))}
        {allergies.length === 0 && <p className="empty-state">No allergies recorded</p>}
      </div>
    </div>
  );
}

export default Allergies;
